import { ToastViewport } from '@vgw/multibrand-fe-toast'
import { useInitUserInactivityManager } from './features/user-inactivity-manager/use-init-user-inactivity-manager'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { Page404 } from './routes/-404/404'
import { initializeGrafanaIntegration } from '@/grafana-integration'
import { useFeatureFlagsOptions } from './lib/use-feature-flag'
import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { getBrandConfig } from './config/config'
import { useBrazeInit } from '@/features/braze/use-braze-init'
import { useBrazeSubscriptions } from '@/features/braze/use-braze-subscriptions'
import { getCoinsStateQueryOptions } from '@/features/coins-state/use-coins-state-query/use-coins-state-query'
import { getOidc } from '@/features/oidc/oidc'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 120_000, // 2 minutes
    },
  },
})

const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultNotFoundComponent: Page404,
  unmaskOnReload: true,
  context: {
    queryClient,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  interface HistoryState {
    from?: string
  }
  interface RouterContext {
    queryClient: QueryClient
  }
}

void initializeGrafanaIntegration(router)

window.addEventListener('message', async (event) => {
  // this message handler used as an escape hatch for e2e tests to invalidate token balances
  // when async event in the system was not delivered yet
  if (event.data.type === 'INVALIDATE_BALANCE') {
    void queryClient.invalidateQueries(getCoinsStateQueryOptions())
  }

  // the way to quicker get fresh token from e2e tests
  if (event.data.type === 'RENEW_ACCESS_TOKEN') {
    const oidc = await getOidc()
    if (oidc.isUserLoggedIn) {
      oidc.renewTokens()
    }
  }
})

const AppContent = () => {
  useBrazeInit()
  useBrazeSubscriptions({ router })
  useInitUserInactivityManager({ router })
  const featureFlagsOptions = useFeatureFlagsOptions()

  useEffect(() => {
    void queryClient.prefetchQuery({ ...featureFlagsOptions, gcTime: Infinity })
  }, [featureFlagsOptions])

  const { name } = getBrandConfig()

  return (
    <>
      <title>{name}</title>
      <ToastViewport className="fixed top-16 left-1/2 z-9999 w-[calc(100%-1rem)] -translate-x-1/2" />
      <RouterProvider router={router} />
    </>
  )
}

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  )
}
